import { useState, useCallback, useRef, useEffect } from 'react';
import { getResultCacheWindow, getResultCacheMetadata } from '../services/resultCacheApi';
import { BUFFER_SIZE, REFETCH_MARGIN, INITIAL_FETCH_SIZE, WINDOW_BUDGET_BYTES, MIN_BUFFER_SIZE, estimateWindowBytes } from './windowBudget';
import { computeSkeletonRanges, type SkeletonRanges } from './skeletonRanges';

type BufferState = {
  /** First row index stored in `rows` */
  startOffset: number;
  /** Buffered rows */
  rows: Record<string, unknown>[];
};

const EMPTY_BUFFER: BufferState = { startOffset: 0, rows: [] };

type UseWindowedDataParams = {
  /** Cache URI returned by execute-query-v2 */
  cacheUri: string | undefined;
  /** Total rows in the cached result set (may change after filter) */
  totalRowCount: number;
  /** Increment to force buffer re-fetch (e.g., after sort/filter changes data order) */
  dataVersion?: number;
  /** Monotonically increasing counter — bumped on each query execution so buffer resets on re-execution */
  cacheGeneration?: number;
  /** Latest source generation from sort/filter responses — used to discard stale getWindow responses */
  sourceGeneration?: number;
};

/** Error info surfaced when the underlying ResultSource enters the 'failed' state */
export type SourceError = {
  message: string;
};

type UseWindowedDataResult = {
  /** Get the visible slice of rows for the given absolute index range */
  getVisibleRows: (startIndex: number, endIndex: number) => Record<string, unknown>[];
  /** Call when the viewport range changes (triggers prefetch if needed) */
  onViewportChange: (viewportStart: number, viewportEnd: number) => void;
  /** True while a buffer fetch is in flight */
  isBufferLoading: boolean;
  /** Current buffer start offset */
  bufferStart: number;
  /** Current buffer end offset */
  bufferEnd: number;
  /** All rows currently held in the buffer (for copy/clipboard operations) */
  bufferRows: Record<string, unknown>[];
  /** Compute skeleton ranges for a given viewport range (rows outside buffer that need placeholders) */
  getSkeletonRanges: (startIndex: number, endIndex: number) => SkeletonRanges;
  /** Current dynamic buffer size (may shrink from BUFFER_SIZE for large rows) */
  dynamicBufferSize: number;
  /** Non-null when the result source has entered a failed state */
  sourceError: SourceError | null;
};

/**
 * Manages a sliding window buffer of rows fetched from the Electron result cache.
 * The buffer is centered around the current viewport and prefetches when the
 * viewport approaches the buffer edge.
 */
export function useWindowedData({
  cacheUri,
  totalRowCount,
  dataVersion = 0,
  cacheGeneration = 0,
  sourceGeneration = 0,
}: UseWindowedDataParams): UseWindowedDataResult {
  const [buffer, _setBuffer] = useState<BufferState>(EMPTY_BUFFER);
  // Keep a ref in sync with buffer state so effects can read current value without stale closures
  const bufferRef = useRef<BufferState>(EMPTY_BUFFER);
  const setBuffer = useCallback((next: BufferState) => {
    bufferRef.current = next;
    _setBuffer(next);
  }, []);
  const [isBufferLoading, setIsBufferLoading] = useState(false);
  const [sourceError, setSourceError] = useState<SourceError | null>(null);

  // Monotonically-incrementing counter for unique fetch tokens
  const nextFetchTokenRef = useRef(0);
  // Token of the currently active (in-flight) fetch — 0 means idle.
  // Only the fetch whose myToken matches this value may mutate state in finally.
  // Reset effects clear this to 0 to allow a new fetch to start immediately.
  const activeFetchTokenRef = useRef(0);
  // Track the last requested center to avoid redundant fetches
  const lastFetchCenterRef = useRef<number>(-1);
  // Track whether the hook is still mounted — used to ignore in-flight fetch responses after unmount
  const mountedRef = useRef(true);
  // Track the cacheGeneration at fetch time to discard stale responses from superseded executions
  const cacheGenerationRef = useRef(cacheGeneration);
  // Track the latest known source generation (from sort/filter responses) to discard stale getWindow responses
  const sourceGenerationRef = useRef(sourceGeneration);
  // Track the latest cacheUri to discard responses from a previously active source
  const cacheUriRef = useRef(cacheUri);
  // Track the latest totalRowCount via a ref so fetchBufferAround/onViewportChange
  // can read it WITHOUT listing it as a useCallback dep. Otherwise totalRowCount
  // (which ticks on every population-progress render of a streaming windowed
  // query) recreates both callbacks every render → ResultsGrid's
  // `[..., windowViewportChange]` effect re-fires every render → setState →
  // re-render → unbounded loop that intermittently wedges the renderer JS
  // thread >120s. The regression test "scrolling with stable onViewportChange
  // callback" asserts exactly this stability. (cf. Docs/QueryEditor_CpuIssue.md)
  const totalRowCountRef = useRef(totalRowCount);
  // Queue the latest viewport center when a fetch is already in flight (latest-wins)
  const queuedCenterRef = useRef<number | null>(null);
  // Dynamic buffer size — shrinks from BUFFER_SIZE when per-row bytes are large
  const dynamicBufferSizeRef = useRef(BUFFER_SIZE);
  // Track whether we already logged the shrink for this cacheGeneration
  const shrinkLoggedRef = useRef(false);
  // Track whether the initial (probe) fetch has completed for this cacheGeneration
  const initialFetchCompleteRef = useRef(false);

  // Keep sourceGenerationRef in sync with prop
  sourceGenerationRef.current = sourceGeneration;
  cacheUriRef.current = cacheUri;
  totalRowCountRef.current = totalRowCount;

  // Mark as mounted on init; clean up on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reset buffer when cacheUri or cacheGeneration changes (new query execution)
  useEffect(() => {
    cacheGenerationRef.current = cacheGeneration;
    setBuffer(EMPTY_BUFFER);
    lastFetchCenterRef.current = -1;
    activeFetchTokenRef.current = 0; // invalidate any in-flight fetch, allow new fetch
    queuedCenterRef.current = null;
    dynamicBufferSizeRef.current = BUFFER_SIZE;
    shrinkLoggedRef.current = false;
    initialFetchCompleteRef.current = false;
    setSourceError(null);

    if (cacheUri && totalRowCount > 0) {
      // Fetch initial window
      fetchBufferAround(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheUri, cacheGeneration]);

  // When totalRowCount arrives after cacheUri (race condition fix):
  // If buffer is empty, no fetch is active, and cacheUri is set, trigger the initial fetch.
  // The activeFetchTokenRef guard prevents this from queuing a redundant fetch when
  // the cacheUri/cacheGeneration effect already started one in the same render cycle.
  // Uses bufferRef (not buffer state) to avoid stale closure during rapid totalRowCount bumps.
  useEffect(() => {
    if (!cacheUri || totalRowCount <= 0) return;
    if (bufferRef.current.rows.length > 0) return;
    if (activeFetchTokenRef.current > 0) return;
    fetchBufferAround(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchBufferAround is defined later via useCallback; safe to omit since cacheUri captures its identity
  }, [totalRowCount, cacheUri]);

  // Re-fetch buffer when dataVersion changes (sort/filter applied — data order changed)
  useEffect(() => {
    if (dataVersion === 0 || !cacheUri) return;
    setBuffer(EMPTY_BUFFER);
    lastFetchCenterRef.current = -1;
    activeFetchTokenRef.current = 0; // invalidate any in-flight fetch, allow new fetch
    queuedCenterRef.current = null;
    fetchBufferAround(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const fetchBufferAround = useCallback(
    async (centerIndex: number) => {
      if (!cacheUriRef.current) return;

      // If a fetch is already in flight, queue this center (latest wins — no concurrent IPC)
      if (activeFetchTokenRef.current > 0) {
        queuedCenterRef.current = centerIndex;
        return;
      }

      const myToken = ++nextFetchTokenRef.current;
      activeFetchTokenRef.current = myToken;

      const isProbe = !initialFetchCompleteRef.current;
      const total = totalRowCountRef.current;
      // Skip the small probe when total fits in the buffer — single round-trip
      const currentBufferSize = isProbe
        ? (total <= dynamicBufferSizeRef.current ? total : Math.min(INITIAL_FETCH_SIZE, total))
        : dynamicBufferSizeRef.current;
      const halfBuffer = Math.floor(currentBufferSize / 2);
      const start = Math.max(0, centerIndex - halfBuffer);
      const count = Math.min(currentBufferSize, total - start);
      if (count <= 0) {
        activeFetchTokenRef.current = 0;
        return;
      }

      // Capture generation and URI before async call to detect stale responses
      const genAtFetchTime = cacheGenerationRef.current;
      const uriAtFetchTime = cacheUriRef.current;

      lastFetchCenterRef.current = centerIndex;
      setIsBufferLoading(true);

      try {
        const response = await getResultCacheWindow({
          uri: uriAtFetchTime,
          startIndex: start,
          count,
          expectedGeneration: sourceGenerationRef.current,
        });
        // If this fetch was superseded by a reset effect, bail out
        if (activeFetchTokenRef.current !== myToken) return;
        // Ignore response if unmounted or response is undefined (race with tab close)
        if (!mountedRef.current || !response) return;
        // Discard stale response from a superseded execution
        if (cacheGenerationRef.current !== genAtFetchTime) return;
        // Discard stale response from a different source (user switched result set/tab)
        if (cacheUriRef.current !== uriAtFetchTime) return;
        // Discard stale response from a sort/filter that happened after this fetch started
        if (response.generation < sourceGenerationRef.current) {
          // Re-fetch with current viewport to get post-sort/filter data
          queuedCenterRef.current = centerIndex;
          return;
        }
        setBuffer({ startOffset: start, rows: response.rows });

        // Dynamic buffer sizing — shrink if projected payload exceeds 50MB budget
        if (response.rows.length > 0) {
          const estimatedBytes = estimateWindowBytes(response.rows);
          const perRowBytes = estimatedBytes / response.rows.length;
          if (perRowBytes * dynamicBufferSizeRef.current > WINDOW_BUDGET_BYTES) {
            const newSize = Math.max(MIN_BUFFER_SIZE, Math.floor(WINDOW_BUDGET_BYTES / perRowBytes));
            dynamicBufferSizeRef.current = newSize;
            if (!shrinkLoggedRef.current) {
              console.info(
                `[useWindowedData] shrunk buffer from ${BUFFER_SIZE} to ${newSize} rows (~${Math.round(perRowBytes / 1024)} KB/row)`
              );
              shrinkLoggedRef.current = true;
            }
          }
        }

        // After the probe fetch, schedule a follow-up to fill the full buffer
        if (isProbe) {
          initialFetchCompleteRef.current = true;
          if (response.rows.length < total) {
            queuedCenterRef.current = centerIndex;
          }
        }
      } catch (err) {
        if (!mountedRef.current) return;
        if (activeFetchTokenRef.current !== myToken) return;
        try {
          const meta = await getResultCacheMetadata({ uri: uriAtFetchTime });
          if (!mountedRef.current) return;
          if (meta.state === 'failed') {
            setSourceError({ message: meta.errorMessage ?? 'Result source failed. Re-run the query to see results.' });
            return;
          }
        } catch {
          // metadata fetch also failed — surface original error
        }
        setSourceError({ message: (err instanceof Error ? err.message : String(err)) || 'Result source failed. Re-run the query to see results.' });
      } finally {
        // Only the active fetch's finally clears loading and drains the queue.
        // A superseded fetch (reset effect cleared activeFetchTokenRef) skips this
        // entirely — preventing it from clobbering the new fetch's state.
        if (activeFetchTokenRef.current === myToken) {
          activeFetchTokenRef.current = 0;
          if (mountedRef.current) {
            setIsBufferLoading(false);
            // Drain queued center — if user scrolled during the fetch, fetch the latest position
            const queued = queuedCenterRef.current;
            if (queued !== null) {
              queuedCenterRef.current = null;
              fetchBufferAround(queued);
            }
          }
        }
      }
    },
    // Stable identity: cacheUri/totalRowCount are read via refs (above), NOT
    // listed here — otherwise the per-population-tick totalRowCount churn
    // recreates this callback every render and storms ResultsGrid's
    // viewport effect. setBuffer is itself stable (useCallback []).
    [setBuffer]
  );

  const getVisibleRows = useCallback(
    (startIndex: number, endIndex: number): Record<string, unknown>[] => {
      const { startOffset, rows } = buffer;
      const bufferEnd = startOffset + rows.length;

      // Clamp to buffer bounds
      const clampedStart = Math.max(startIndex, startOffset);
      const clampedEnd = Math.min(endIndex, bufferEnd);

      if (clampedStart >= clampedEnd) return [];
      return rows.slice(clampedStart - startOffset, clampedEnd - startOffset);
    },
    [buffer]
  );

  const onViewportChange = useCallback(
    (viewportStart: number, viewportEnd: number) => {
      if (!cacheUriRef.current || totalRowCountRef.current === 0) return;

      const { startOffset, rows } = bufferRef.current;
      const bufferEndOffset = startOffset + rows.length;

      // Initial fetch hasn't completed yet
      if (rows.length === 0) return;

      const distFromStart = viewportStart - startOffset;
      const distFromEnd = bufferEndOffset - viewportEnd;

      if (distFromStart < REFETCH_MARGIN || distFromEnd < REFETCH_MARGIN) {
        const center = Math.floor((viewportStart + viewportEnd) / 2);
        // Avoid refetching if we'd land in roughly the same spot
        if (Math.abs(center - lastFetchCenterRef.current) > REFETCH_MARGIN / 2) {
          fetchBufferAround(center);
        }
      }
    },
    // Stable identity (fetchBufferAround is now stable; cacheUri/totalRowCount
    // read via refs). This is the contract the "scrolling with stable
    // onViewportChange callback (regression)" e2e test asserts.
    [fetchBufferAround]
  );

  const getSkeletonRanges = useCallback(
    (viewStart: number, viewEnd: number): SkeletonRanges =>
      computeSkeletonRanges(viewStart, viewEnd, buffer.startOffset, buffer.rows.length),
    [buffer]
  );

  return {
    getVisibleRows,
    onViewportChange,
    isBufferLoading,
    bufferStart: buffer.startOffset,
    bufferEnd: buffer.startOffset + buffer.rows.length,
    bufferRows: buffer.rows,
    getSkeletonRanges,
    dynamicBufferSize: dynamicBufferSizeRef.current,
    sourceError,
  };
}

export { BUFFER_SIZE, REFETCH_MARGIN, INITIAL_FETCH_SIZE, WINDOW_BUDGET_BYTES, MIN_BUFFER_SIZE, estimateWindowBytes };
export type { SkeletonRanges };
